import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  createSchedule,
  deleteSchedule,
  fetchDeliverySchedules,
  fetchSchedules,
  JOURNAL_SCHEDULE_COLOR,
  ScheduleRecord,
} from './scheduleApi';

/* ==========================================
   Data & Hooks
========================================== */
export interface CalendarEvent extends ScheduleRecord {}

const CALENDAR_EVENTS_CHANGED = 'neurit-calendar-events-changed';
const isJournalEvent = (event: CalendarEvent) =>
  event.title.startsWith('📔');
const isDeliveryEvent = (event: CalendarEvent) =>
  event.memo?.startsWith('delivery:') || event.id.startsWith('delivery-');
const displayEventTitle = (event: CalendarEvent) => event.title.replace(/^📔\s*/, '');
const eventMarkerColor = (event: CalendarEvent) => isJournalEvent(event) ? JOURNAL_SCHEDULE_COLOR : (event.color || '#65A3FF');

const JournalNoteIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{ flex: `0 0 ${size}px` }}
  >
    <rect x="5" y="3" width="15" height="18" rx="3" fill="#FFF0E8" stroke="#E07040" strokeWidth="1.7" />
    <path d="M9 3v18" stroke="#E07040" strokeWidth="1.7" />
    <path d="M12 8h5M12 12h5M12 16h3.5" stroke="#E89A72" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M3.5 7h3M3.5 12h3M3.5 17h3" stroke="#C98A52" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const EventTitleContent: React.FC<{ event: CalendarEvent }> = ({ event }) => (
  <>
    {isJournalEvent(event) && <JournalNoteIcon />}
    <span>{displayEventTitle(event)}</span>
  </>
);

export const useCalendarEvents = () => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const loadEvents = async () => {
      try {
        const schedules = await fetchSchedules(controller.signal);
        const deliveryEvents = await fetchDeliverySchedules(controller.signal).catch(() => []);
        setEvents([...schedules, ...deliveryEvents]);
        setError(null);
      } catch (e) {
        if ((e as DOMException)?.name !== 'AbortError') {
          setError(e instanceof Error ? e.message : '일정을 불러오지 못했습니다.');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    const handleEventsChanged = (event: Event) => {
      const nextEvents = (event as CustomEvent<CalendarEvent[]>).detail;
      if (Array.isArray(nextEvents)) setEvents(nextEvents);
    };
    loadEvents();
    const refreshTimer = window.setInterval(loadEvents, 60000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') loadEvents();
    };
    window.addEventListener(CALENDAR_EVENTS_CHANGED, handleEventsChanged);
    window.addEventListener('neurit-calendar-refresh', loadEvents);
    window.addEventListener('focus', loadEvents);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      controller.abort();
      window.clearInterval(refreshTimer);
      window.removeEventListener(CALENDAR_EVENTS_CHANGED, handleEventsChanged);
      window.removeEventListener('neurit-calendar-refresh', loadEvents);
      window.removeEventListener('focus', loadEvents);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const publishEvents = useCallback((updated: CalendarEvent[]) => {
    setEvents(updated);
    window.dispatchEvent(new CustomEvent(CALENDAR_EVENTS_CHANGED, { detail: updated }));
  }, []);

  const addEvent = useCallback(async (date: string, title: string) => {
    setError(null);
    try {
      const created = await createSchedule(date, title);
      const updated = created
        ? [...events, created]
        : [...await fetchSchedules(), ...events.filter(isDeliveryEvent)];
      publishEvents(updated);
    } catch (e) {
      const message = e instanceof Error ? e.message : '일정을 저장하지 못했습니다.';
      setError(message);
      throw e;
    }
  }, [events, publishEvents]);

  const removeEvent = useCallback(async (id: string) => {
    setError(null);
    try {
      await deleteSchedule(id);
      publishEvents(events.filter(event => event.id !== id));
    } catch (e) {
      const message = e instanceof Error ? e.message : '일정을 삭제하지 못했습니다.';
      setError(message);
      throw e;
    }
  }, [events, publishEvents]);

  return { events, loading, error, addEvent, removeEvent };
};

/* ==========================================
   Widget Components
========================================== */
const WidgetWrapper = styled(motion.div)`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  background: transparent;
`;

const SmallCal = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  color: #3D3020;
`;

const MonthText = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: #E07040;
`;

const DateText = styled.div`
  font-size: 42px;
  font-weight: 800;
  line-height: 1.1;
  margin: 4px 0;
`;

const DayText = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #7A6A50;
`;

const SmallEvents = styled.div`
  width: 100%;
  margin-top: 8px;
  padding-top: 7px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SmallEventLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  color: #5C5040;
  font-size: 11px;
  font-weight: 600;

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    flex: 0 0 5px;
    border-radius: 50%;
    background: #E07040;
  }

  span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

const MediumCal = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 10px;
  padding: 0 10px;
`;

const MediumTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 94px;
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 60px;
  border-right: 1px solid rgba(0,0,0,0.1);
  padding-right: 16px;
`;

const RightSection = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: stretch;
  gap: 4px;
  min-width: 0;
`;

const MiniDay = styled.div<{ $isToday: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 1;
  min-width: 0;

  .dow {
    font-size: 11px;
    font-weight: 600;
    color: ${p => p.$isToday ? '#E07040' : '#888'};
  }
  .num {
    font-size: 14px;
    font-weight: 700;
    color: ${p => p.$isToday ? '#fff' : '#333'};
    background: ${p => p.$isToday ? '#E07040' : 'transparent'};
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }
  .dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #65A3FF;
  }
`;

const AgendaPanel = styled.div<{ $compact?: boolean }>`
  width: 100%;
  min-height: ${p => p.$compact ? '48px' : '62px'};
  padding: ${p => p.$compact ? '7px 10px' : '9px 12px'};
  border-radius: 12px;
  background: rgba(101, 163, 255, 0.08);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  overflow: hidden;
`;

const AgendaItem = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 7px;
  min-width: 0;
  color: #536781;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.25;

  .date {
    color: #E07040;
    font-weight: 700;
    white-space: nowrap;
  }
  .title {
    display: flex;
    align-items: center;
    gap: 6px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .title span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

const EmptyAgenda = styled.div`
  color: #A39A8C;
  font-size: 10px;
  text-align: center;
`;

const LargeCal = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const MonthHeader = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #E07040;
  margin-bottom: 12px;
  padding-left: 4px;
`;

const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  flex: 1;
  min-height: 0;

  .dow {
    font-size: 11px;
    font-weight: 700;
    color: #888;
    text-align: center;
    margin-bottom: 4px;
  }
  .cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding-top: 4px;
    font-size: 12px;
    font-weight: 600;
    color: #444;
    border-radius: 8px;
    background: rgba(0,0,0,0.02);
    position: relative;
    min-width: 0;
    overflow: hidden;

    &.today {
      background: #E07040;
      color: #fff;
    }
  }
  .empty {
    background: transparent;
  }
  .dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #65A3FF;
    margin-top: 2px;
  }
`;

export const CalendarWidget: React.FC<{ size: 'small'|'medium'|'large', onClick?: () => void }> = ({ size, onClick }) => {
  const { events } = useCalendarEvents();
  const today = new Date();
  const todayStr = today.getFullYear() + '-' + String(today.getMonth()+1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
  const todaysEvents = events.filter(e => e.date === todayStr);

  const dayName = today.toLocaleDateString('ko-KR', { weekday: 'long' });
  const dateNum = today.getDate();
  const weekStart = new Date(today);
  weekStart.setHours(0, 0, 0, 0);
  weekStart.setDate(today.getDate() - today.getDay());
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const weeklyEvents = events
    .filter(event => {
      const eventDate = new Date(`${event.date}T00:00:00`);
      return eventDate >= weekStart && eventDate <= weekEnd;
    })
    .sort((a, b) => a.date.localeCompare(b.date));
  const monthlyEvents = events
    .filter(event => event.date.startsWith(
      `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
    ))
    .sort((a, b) => a.date.localeCompare(b.date));
  const agendaDate = (date: string) => {
    const parsed = new Date(`${date}T00:00:00`);
    return `${parsed.getMonth() + 1}/${parsed.getDate()} ${['일', '월', '화', '수', '목', '금', '토'][parsed.getDay()]}`;
  };

  return (
    <WidgetWrapper onClick={onClick} whileTap={{ scale: 0.96 }}>
      {size === 'small' && (
        <SmallCal>
          <MonthText>{today.getMonth() + 1}월</MonthText>
          <DateText>{dateNum}</DateText>
          <DayText>{dayName}</DayText>
          <SmallEvents>
            {todaysEvents.length > 0 ? (
              <>
              {todaysEvents.slice(0, 2).map((event, index) => (
                <SmallEventLine key={`${event.id}-${event.date}-${index}`}><EventTitleContent event={event} /></SmallEventLine>
              ))}
              {todaysEvents.length > 2 && (
                <div style={{ color: '#A09070', fontSize: '9px', textAlign: 'right' }}>+{todaysEvents.length - 2}개 더</div>
              )}
              </>
            ) : (
              <EmptyAgenda>오늘 등록된 일정이 없습니다.</EmptyAgenda>
            )}
          </SmallEvents>
        </SmallCal>
      )}
      {size === 'medium' && (
        <MediumCal>
          <MediumTopRow>
            <LeftSection>
              <MonthText>{today.getMonth() + 1}월</MonthText>
              <DateText>{dateNum}</DateText>
              <DayText>{dayName.replace('요일', '')}</DayText>
            </LeftSection>
            <RightSection>
              {Array.from({length: 7}).map((_, i) => {
                 const d = new Date(today);
                 d.setDate(today.getDate() - today.getDay() + i);
                 const isToday = d.toDateString() === today.toDateString();
                 const dStr = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
                 const hasEvents = events.some(event => event.date === dStr);
                 return (
                   <MiniDay key={i} $isToday={isToday}>
                     <span className="dow">{['일','월','화','수','목','금','토'][i]}</span>
                     <span className="num">{d.getDate()}</span>
                     {hasEvents && <div className="dot" />}
                   </MiniDay>
                 )
              })}
            </RightSection>
          </MediumTopRow>
          <AgendaPanel $compact>
            {weeklyEvents.length === 0 ? (
              <EmptyAgenda>이번 주에 등록된 일정이 없습니다.</EmptyAgenda>
            ) : (
              weeklyEvents.slice(0, 2).map((event, index) => (
                <AgendaItem key={`${event.id}-${event.date}-${index}`}>
                  <span className="date">{agendaDate(event.date)}</span>
                  <span className="title"><EventTitleContent event={event} /></span>
                </AgendaItem>
              ))
            )}
          </AgendaPanel>
        </MediumCal>
      )}
      {size === 'large' && (
         <LargeCal>
            <MonthHeader>{today.getFullYear()}년 {today.getMonth() + 1}월</MonthHeader>
            <MonthGrid>
               {['일','월','화','수','목','금','토'].map(d => <div key={d} className="dow">{d}</div>)}
               {(() => {
                 const d = new Date(today.getFullYear(), today.getMonth(), 1);
                 const firstDay = d.getDay();
                 const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
                 const cells = [];
                 for(let i=0; i<firstDay; i++) cells.push(<div key={`empty-${i}`} className="cell empty" />);
                 for(let i=1; i<=daysInMonth; i++) {
                   const cellDate = new Date(today.getFullYear(), today.getMonth(), i);
                   const isToday = cellDate.toDateString() === today.toDateString();
                   const cStr = cellDate.getFullYear() + '-' + String(cellDate.getMonth()+1).padStart(2, '0') + '-' + String(cellDate.getDate()).padStart(2, '0');
                   const dayEvents = events.filter(e => e.date === cStr);
                   cells.push(
                     <div key={i} className={`cell ${isToday ? 'today' : ''}`}>
                       {i}
                       {dayEvents.length > 0 && <div className="dot" style={{ background: isToday ? '#fff' : eventMarkerColor(dayEvents[0])}} />}
                     </div>
                   );
                 }
                 return cells;
               })()}
            </MonthGrid>
            <AgendaPanel style={{ marginTop: '8px' }}>
              {monthlyEvents.length === 0 ? (
                <EmptyAgenda>이번 달에 등록된 일정이 없습니다.</EmptyAgenda>
              ) : (
                monthlyEvents.slice(0, 3).map((event, index) => (
                  <AgendaItem key={`${event.id}-${event.date}-${index}`}>
                    <span className="date">{agendaDate(event.date)}</span>
                    <span className="title"><EventTitleContent event={event} /></span>
                  </AgendaItem>
                ))
              )}
            </AgendaPanel>
         </LargeCal>
      )}
    </WidgetWrapper>
  );
}


/* ==========================================
   Full Screen Calendar Component
========================================== */
const Overlay = styled(motion.div)`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(240, 235, 220, 0.98);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  padding: 40px 24px;
  box-sizing: border-box;
  overflow-y: auto;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;

  h1 {
    font-family: 'Jua', sans-serif;
    font-size: 28px;
    color: #3D3020;
    margin: 0;
  }
`;

const CloseBtn = styled.button`
  background: rgba(0,0,0,0.05);
  border: none;
  width: 40px; height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #333;
  transition: background 0.2s;
  &:hover { background: rgba(0,0,0,0.1); }
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 0 16px;
  
  button {
    background: none; border: none; font-size: 20px; cursor: pointer; color: #E07040;
    padding: 8px; border-radius: 50%;
    &:hover { background: rgba(224, 112, 64, 0.1); }
  }
  span {
    font-size: 18px; font-weight: 700; color: #5C5040;
  }
`;

const FullMonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  background: #fff;
  border-radius: 24px;
  padding: 24px 16px;
  box-shadow: 0 16px 40px rgba(0,0,0,0.04);
  margin-bottom: 24px;
`;

const DayName = styled.div<{ $isWeekend?: boolean }>`
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.$isWeekend ? '#D06060' : '#888'};
  margin-bottom: 12px;
`;

const DayCell = styled.div<{ $isToday: boolean; $isSelected: boolean; $isCurrentMonth: boolean }>`
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 8px;
  border-radius: 16px;
  font-size: 15px;
  font-weight: ${p => p.$isSelected || p.$isToday ? '700' : '500'};
  color: ${p => !p.$isCurrentMonth ? '#ccc' : p.$isSelected ? '#fff' : p.$isToday ? '#E07040' : '#444'};
  background: ${p => p.$isSelected ? '#E07040' : p.$isToday ? 'rgba(224, 112, 64, 0.15)' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  &:hover {
    background: ${p => p.$isSelected ? '#E07040' : p.$isToday ? 'rgba(224, 112, 64, 0.25)' : 'rgba(0,0,0,0.03)'};
  }
`;

const EventMarkerContainer = styled.div`
  display: flex;
  gap: 3px;
  margin-top: 4px;
`;

const Dot = styled.div<{ $color?: string }>`
  width: 5px; height: 5px; border-radius: 50%;
  background: ${p => p.$color || '#65A3FF'};
`;

const EventsListPanel = styled.div`
  background: #fff;
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 16px 40px rgba(0,0,0,0.04);
  flex: 1;
`;

const PanelHeader = styled.div`
  font-size: 18px; font-weight: 700; color: #3D3020; margin-bottom: 16px;
  display: flex; align-items: center; justify-content: space-between;
`;

const AddBtn = styled.button`
  background: #E07040; color: #fff; border: none; border-radius: 12px;
  padding: 8px 16px; font-size: 14px; font-weight: 700; cursor: pointer;
  transition: transform 0.15s;
  &:active { transform: scale(0.95); }
`;

const EventItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: #F9F8F4;
  border-radius: 16px;
  margin-bottom: 12px;

  .title { display: flex; align-items: center; gap: 7px; font-size: 15px; font-weight: 600; color: #444; }
  .del { background: none; border: none; color: #D06060; cursor: pointer; font-size: 18px; padding: 4px; }
`;

const LinkedEventBadge = styled.span`
  flex: 0 0 auto;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(79, 159, 196, 0.12);
  color: #4F8EAC;
  font-size: 10px;
  font-weight: 700;
`;

const CalendarStatus = styled.div<{ $error?: boolean }>`
  padding: 10px 12px;
  margin-bottom: 12px;
  border-radius: 12px;
  background: ${p => p.$error ? 'rgba(208, 96, 96, 0.1)' : 'rgba(101, 163, 255, 0.08)'};
  color: ${p => p.$error ? '#B34D4D' : '#60758E'};
  font-size: 13px;
  text-align: center;
`;

/* ─── Custom Prompt Modal ─── */
const PromptOverlay = styled(motion.div)`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(2px);
`;

const PromptBox = styled(motion.div)`
  background: #FDFCF8;
  border-radius: 20px;
  padding: 24px;
  width: 320px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
`;

const PromptTitle = styled.h3`
  font-size: 17px;
  color: #3D3020;
  margin-bottom: 16px;
  font-weight: 600;
`;

const PromptInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1.5px solid #E8E4D4;
  font-size: 15px;
  outline: none;
  background: #F8F6F0;
  transition: border-color 0.2s;
  &:focus {
    border-color: #E07040;
  }
`;

const PromptActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
`;

const PromptBtn = styled.button<{ $primary?: boolean }>`
  padding: 10px 20px;
  border-radius: 12px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: ${p => p.$primary ? '#E07040' : '#E8E4D4'};
  color: ${p => p.$primary ? '#FFF' : '#5C5040'};
  transition: transform 0.15s, filter 0.2s;
  
  &:active { transform: scale(0.96); }
  &:hover { filter: brightness(0.95); }
`;

export const FullScreenCalendar: React.FC<{ onClose: () => void, layoutId?: string }> = ({ onClose, layoutId }) => {
  const { events, loading, error, addEvent, removeEvent } = useCalendarEvents();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptValue, setPromptValue] = useState("");
  const [saving, setSaving] = useState(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const selDateStr = selectedDate.getFullYear() + '-' + String(selectedDate.getMonth()+1).padStart(2, '0') + '-' + String(selectedDate.getDate()).padStart(2, '0');

  const selectedEvents = events.filter(e => e.date === selDateStr);

  const handlePrevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const handleNextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const handleAddClick = () => {
    setPromptValue("");
    setShowPrompt(true);
  };

  const submitPrompt = async () => {
    if (!promptValue.trim() || saving) return;
    setSaving(true);
    try {
      await addEvent(selDateStr, promptValue.trim());
      setShowPrompt(false);
    } catch {
      // The hook exposes the API error in the calendar panel.
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (id: string) => {
    try {
      await removeEvent(id);
    } catch {
      // The hook exposes the API error in the calendar panel.
    }
  };

  const renderCells = () => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    
    const cells = [];
    
    // Prev month days
    for(let i=firstDay-1; i>=0; i--) {
      const d = daysInPrevMonth - i;
      cells.push(
        <DayCell key={`prev-${d}`} $isCurrentMonth={false} $isToday={false} $isSelected={false} onClick={() => handlePrevMonth()}>
          {d}
        </DayCell>
      );
    }
    
    // Current month days
    for(let i=1; i<=daysInMonth; i++) {
      const cellDate = new Date(year, month, i);
      const isToday = cellDate.toDateString() === new Date().toDateString();
      const isSelected = cellDate.toDateString() === selectedDate.toDateString();
      const cStr = cellDate.getFullYear() + '-' + String(cellDate.getMonth()+1).padStart(2, '0') + '-' + String(cellDate.getDate()).padStart(2, '0');
      const dayEvents = events.filter(e => e.date === cStr);

      cells.push(
        <DayCell 
          key={`curr-${i}`} 
          $isCurrentMonth={true} 
          $isToday={isToday} 
          $isSelected={isSelected}
          onClick={() => setSelectedDate(cellDate)}
        >
          {i}
          {dayEvents.length > 0 && (
            <EventMarkerContainer>
              {dayEvents.slice(0,3).map((e, idx) => <Dot key={`${e.id}-${e.date}-${idx}`} $color={isToday ? '#fff' : eventMarkerColor(e)} />)}
            </EventMarkerContainer>
          )}
        </DayCell>
      );
    }
    return cells;
  };

  return (
    <AnimatePresence>
      <Overlay
        layoutId={layoutId}
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: 'spring', stiffness: 340, damping: 30 }}
      >
        <HeaderRow>
          <h1>캘린더</h1>
          <CloseBtn onClick={onClose}>✕</CloseBtn>
        </HeaderRow>

        <NavRow>
          <button onClick={handlePrevMonth}>◀</button>
          <span>{year}년 {month + 1}월</span>
          <button onClick={handleNextMonth}>▶</button>
        </NavRow>

        <FullMonthGrid>
          {['일','월','화','수','목','금','토'].map((d, i) => (
            <DayName key={d} $isWeekend={i === 0 || i === 6}>{d}</DayName>
          ))}
          {renderCells()}
        </FullMonthGrid>

        <EventsListPanel>
          <PanelHeader>
            <span>{selectedDate.getDate()}일 일정</span>
            <AddBtn onClick={handleAddClick}>+ 추가</AddBtn>
          </PanelHeader>

          {loading && <CalendarStatus>일정을 불러오는 중입니다.</CalendarStatus>}
          {error && <CalendarStatus $error>{error}</CalendarStatus>}

          {!loading && selectedEvents.length === 0 ? (
            <div style={{ padding: '32px 0', textAlign: 'center', color: '#999', fontSize: '14px' }}>
              등록된 일정이 없습니다.
            </div>
          ) : (
            selectedEvents.map((e, index) => (
              <EventItem key={`${e.id}-${e.date}-${index}`}>
                <span className="title"><EventTitleContent event={e} /></span>
                {isDeliveryEvent(e) ? (
                  <LinkedEventBadge>편지 연동</LinkedEventBadge>
                ) : (
                  <button className="del" onClick={() => handleRemove(e.id)} aria-label={`${e.title} 일정 삭제`}>×</button>
                )}
              </EventItem>
            ))
          )}
        </EventsListPanel>
      </Overlay>

      {showPrompt && (
        <PromptOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowPrompt(false)}
        >
          <PromptBox
            initial={{ scale: 0.9, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 10 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <PromptTitle>{selectedDate.getMonth() + 1}월 {selectedDate.getDate()}일의 일정을 입력하세요</PromptTitle>
            <PromptInput
              autoFocus
              value={promptValue}
              onChange={e => setPromptValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && submitPrompt()}
              placeholder="예: 회의 참석, 친구와 저녁 식사"
              maxLength={255}
              disabled={saving}
            />
            {error && <CalendarStatus $error style={{ marginTop: '12px', marginBottom: 0 }}>{error}</CalendarStatus>}
            <PromptActions>
              <PromptBtn onClick={() => setShowPrompt(false)} disabled={saving}>취소</PromptBtn>
              <PromptBtn $primary onClick={submitPrompt} disabled={saving}>{saving ? '저장 중...' : '확인'}</PromptBtn>
            </PromptActions>
          </PromptBox>
        </PromptOverlay>
      )}
    </AnimatePresence>
  );
};
